import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, X } from "lucide-react";
import api, { imgUrl, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const EMPTY = {
  name: "", sku: "", category_id: "", description: "", price: "", sale_price: "",
  images: [], video_url: "", dimensions: "", weight: "", material: "", finish: "",
  stock: 0, featured: false, active: true, seo_title: "", seo_description: "",
  bestseller: false, new_arrival: false, sort_order: 0, seo_image: "",
};

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    if (id) {
      api.get("/admin/products").then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (p) setForm({ ...EMPTY, ...p, sale_price: p.sale_price || "" });
      }).catch(() => {});
    }
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImages = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Images uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.category_id) return toast.error("Please select a category");
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
      stock: parseInt(form.stock) || 0,
    };
    try {
      if (id) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(id ? "Product updated" : "Product added");
      navigate("/admin/products");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="product-form">
      <h1 className="font-serif text-3xl font-semibold mb-8">{id ? "Edit Product" : "Add Product"}</h1>
      <form onSubmit={save} className="grid md:grid-cols-[1fr_320px] gap-8">
        <div className="flex flex-col gap-6 bg-white border border-black/10 p-6">
          <div>
            <Label className="text-xs uppercase tracking-widest">Product Name *</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2" data-testid="pf-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Category *</Label>
              <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                <SelectTrigger className="mt-2 rounded-sm" data-testid="pf-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`pf-cat-${c.slug}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">SKU / Product Code</Label>
              <Input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="mt-2" data-testid="pf-sku" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Description</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-2" data-testid="pf-description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Price (₹) *</Label>
              <Input required type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-2" data-testid="pf-price" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Sale Price (₹, optional)</Label>
              <Input type="number" min="0" step="0.01" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} className="mt-2" data-testid="pf-sale-price" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Stock Quantity</Label>
              <Input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="mt-2" data-testid="pf-stock" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Video URL (optional)</Label>
              <Input placeholder="https://youtube.com/embed/..." value={form.video_url} onChange={(e) => set("video_url", e.target.value)} className="mt-2" data-testid="pf-video" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Dimensions</Label>
              <Input placeholder="e.g. 12 x 12 x 20 cm" value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} className="mt-2" data-testid="pf-dimensions" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Weight</Label>
              <Input placeholder="e.g. 850 g" value={form.weight} onChange={(e) => set("weight", e.target.value)} className="mt-2" data-testid="pf-weight" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Material</Label>
              <Input placeholder="e.g. Brass, Iron" value={form.material} onChange={(e) => set("material", e.target.value)} className="mt-2" data-testid="pf-material" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Color / Finish</Label>
              <Input placeholder="e.g. Antique Gold" value={form.finish} onChange={(e) => set("finish", e.target.value)} className="mt-2" data-testid="pf-finish" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">SEO Title (optional)</Label>
              <Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className="mt-2" data-testid="pf-seo-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">SEO Description (optional)</Label>
              <Input value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className="mt-2" data-testid="pf-seo-description" />
            </div>
          </div>
          <div className="flex flex-wrap gap-8">
            <label className="flex items-center gap-3 text-sm" data-testid="pf-featured-label">
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="pf-featured" /> Featured on homepage
            </label>
            <label className="flex items-center gap-3 text-sm" data-testid="pf-bestseller-label">
              <Switch checked={form.bestseller} onCheckedChange={(v) => set("bestseller", v)} data-testid="pf-bestseller" /> Bestseller badge
            </label>
            <label className="flex items-center gap-3 text-sm" data-testid="pf-new-arrival-label">
              <Switch checked={form.new_arrival} onCheckedChange={(v) => set("new_arrival", v)} data-testid="pf-new-arrival" /> New arrival badge
            </label>
            <label className="flex items-center gap-3 text-sm" data-testid="pf-active-label">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="pf-active" /> Visible in store
            </label>
            <div className="flex items-center gap-3">
              <Label className="text-xs uppercase tracking-widest">Sort Order</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value) || 0)} className="w-20 h-8" data-testid="pf-sort-order" />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="bg-white border border-black/10 p-6">
            <Label className="text-xs uppercase tracking-widest">Product Images</Label>
            <div className="grid grid-cols-3 gap-2 mt-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative aspect-square border border-black/10 group">
                  <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("images", form.images.filter((_, x) => x !== i))}
                    className="absolute top-1 right-1 bg-white/90 p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-testid={`pf-remove-img-${i}`} aria-label="Remove image">
                    <X strokeWidth={1.5} size={12} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="aspect-square border border-dashed border-black/20 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-gold hover:text-gold transition-colors duration-300"
                data-testid="pf-upload-button">
                <Upload strokeWidth={1.5} size={18} />
                <span className="text-[10px]">{uploading ? "Uploading..." : "Upload"}</span>
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={uploadImages} data-testid="pf-file-input" />
            <p className="text-[11px] text-muted-foreground mt-3">Images are automatically compressed without visible quality loss.</p>
          </div>
          <button type="submit" disabled={saving || uploading}
            className="w-full bg-[#111] text-[#FAF9F6] py-4 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50"
            data-testid="pf-save">
            {saving ? "Saving..." : id ? "Update Product" : "Add Product"}
          </button>
          <button type="button" onClick={() => navigate("/admin/products")} className="w-full border border-black/20 py-3 text-xs uppercase tracking-widest hover:border-gold transition-colors duration-300" data-testid="pf-cancel">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
